
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { BaseControlledModal } from "./base_controlled_modal";
import { useEffect, useState } from "react";
import { processError } from "../utils/base_error";
import { JobStatusResponse } from "../domain/models/job_status_response";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PrimaryButton from "./primary_button";

/**
 * JobWaiterModal
 */

type Props = {
  open: boolean;
  onClose: () => void;
  onUpdateJobStatus: (callback: (response: JobStatusResponse) => void) => Promise<void>;
  onError: (message: string) => void;
};

export const JobWaiterModal = ({
  open,
  onClose,
  onUpdateJobStatus,
  onError,
}: Props) => {
  const [response, setResponse] = useState<JobStatusResponse>();

  const download = response?.download && new Date(response.download.expiraEm).getTime() > Date.now()
    ? response.download
    : null;

  useEffect(() => {
    if (!open) return;

    setResponse(undefined);
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const finishJob = () => {
      onClose();
    };

    const checkJobStatus = async () => {
      if (cancelled) return;

      try {
        await onUpdateJobStatus(
          response => {
            if (cancelled) return;

            if (response.status === "Concluido") {
              setResponse(response);
            } else if (response.status === "Falhou") {
              finishJob();
              onError(response.mensagem || "Falha ao gerar relatório!");
            } else {
              timer = setTimeout(checkJobStatus, 30000);
            }
          }
        );

      } catch (e: unknown) {
        if (cancelled) return;
        finishJob();
        onError(processError(e, "Falha ao consultar status do relatório!")[0]);
      }
    };

    timer = setTimeout(checkJobStatus, 5000);

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [
    open,
    onUpdateJobStatus,
    onClose,
    onError,
  ]);

  return (
    <BaseControlledModal
      open={open}
      saving={false}
      loading={false}
      showConfirmButton={false}
      width="32rem"
      cancelButtonLabel="Fechar"
      title={response ? "Tarefa concluída" : "Executando tarefa"}
      onClose={onClose}>
      <Stack spacing={3} alignItems="center" sx={{ py: 2 }} role="status" aria-live="polite">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 72, height: 72, borderRadius: "50%", bgcolor: "action.hover" }}>
          {response ? (
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 40 }} />
          ) : (
            <CircularProgress size={36} aria-label="Executando tarefa" />
          )}
        </Box>

        <Stack spacing={1} textAlign="center">
          <Typography variant="h6">
            {response ? "Tudo pronto!" : "Preparando sua tarefa"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {response
              ? download ? "Seu arquivo está disponível para download." : "A tarefa foi concluída. Não há arquivo disponível para download."
              : "Isso pode levar alguns minutos. O download ficará disponível aqui assim que terminarmos."}
          </Typography>
        </Stack>

        {download && (
          <Stack spacing={2} sx={{ width: "100%" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 2, bgcolor: "action.hover" }}>
              <InsertDriveFileOutlinedIcon color="action" />
              <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                {download.nomeArquivo}
              </Typography>
            </Stack>
            <PrimaryButton
              buttonColor="primary-green"
              startIcon={<DownloadIcon />}
              href={download.url}
              onClick={event => {
                event.preventDefault();
                window.open(download.url, "_blank", "noopener,noreferrer");
              }}>
              Baixar arquivo
            </PrimaryButton>
          </Stack>
        )}
      </Stack>
    </BaseControlledModal>
  );
}
